import * as NotificationsConstants from './constants';

/*
 * Get the badge state of a conversation
 * @param  {Object} space - a space
 * @param  {boolean} [unread=false] - whether this conversation has unread activities
 * @param  {boolean} [hasMention=false] - whether this conversation has unread mentions
 * @param  {string} [globalNotificationState=NotificationsConstants.NOTIFICATIONS_ALL]
 *          - the value of global notification setting
 * @returns {string} a value that indicates what the badge of this conversation should be.
 */
export function getBadgeState({
  space, unread = false, hasMention = false, globalNotificationState = NotificationsConstants.NOTIFICATIONS_ALL
}) {
  let badge = NotificationsConstants.NOTIFICATIONS_BADGE_NONE;

  if (!space) {
    return badge;
  }

  // One on one spaces
  if (space.type === NotificationsConstants.SPACE_TYPE_ONE_ON_ONE) {
    const isMuted = space.tags
      && space.tags.includes(NotificationsConstants.MESSAGE_NOTIFICATIONS_OFF);

    if (isMuted) {
      return NotificationsConstants.NOTIFICATIONS_BADGE_MUTE;
    }
    if (unread) {
      return NotificationsConstants.NOTIFICATIONS_BADGE_UNREAD;
    }

    return badge;
  }

  // Group spaces
  const {
    isMentionNotificationsOn,
    isMentionNotificationsOff,
    isMessageNotificationsOn,
    isMessageNotificationsOff
  } = space;

  if (isMessageNotificationsOff === undefined && isMessageNotificationsOn === undefined
    || isMentionNotificationsOff === undefined && isMentionNotificationsOn === undefined) {
    // No change to room notifications so default to global
    if (hasMention && globalNotificationState !== NotificationsConstants.NOTIFICATIONS_OFF) {
      badge = NotificationsConstants.NOTIFICATIONS_BADGE_MENTION;
    }
    else if (unread && globalNotificationState === NotificationsConstants.NOTIFICATIONS_ALL) {
      badge = NotificationsConstants.NOTIFICATIONS_BADGE_UNREAD;
    }

    return badge;
  }

  switch (globalNotificationState) {
    case NotificationsConstants.NOTIFICATIONS_OFF:
      if (!isMessageNotificationsOff && isMentionNotificationsOn) {
        // All
        if (hasMention) {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_MENTION;
        }
        else if (unread) {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_UNREAD;
        }
        else {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_UNMUTE;
        }
      }
      else if (isMessageNotificationsOff && isMentionNotificationsOn) {
        // Mention only
        if (hasMention) {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_MENTION;
        }
        else {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_UNMUTE;
        }
      }
      else {
        badge = NotificationsConstants.NOTIFICATIONS_BADGE_NONE;
      }
      break;
    case NotificationsConstants.NOTIFICATIONS_ALL:
      if (!isMessageNotificationsOff) {
        // Global or All
        if (hasMention) {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_MENTION;
        }
        else if (unread) {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_UNREAD;
        }
        else {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_NONE;
        }
      }
      else if (isMessageNotificationsOff && isMentionNotificationsOn) {
        // Mention only
        if (hasMention) {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_MENTION;
        }
        else {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_MUTE;
        }
      }
      else {
        // Off
        badge = NotificationsConstants.NOTIFICATIONS_BADGE_MUTE;
      }
      break;
    case NotificationsConstants.NOTIFICATIONS_MENTIONS:
      if (isMessageNotificationsOff === isMentionNotificationsOn) {
        // Global or Mention only
        if (hasMention) {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_MENTION;
        }
        else {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_NONE;
        }
      }
      else if (!isMessageNotificationsOff && isMentionNotificationsOn) {
        // All
        if (hasMention) {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_MENTION;
        }
        else if (unread) {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_UNREAD;
        }
        else {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_UNMUTE;
        }
      }
      else if (isMessageNotificationsOff && isMentionNotificationsOn) {
        // Mention only
        if (hasMention) {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_MENTION;
        }
        else {
          badge = NotificationsConstants.NOTIFICATIONS_BADGE_NONE;
        }
      }
      else if (isMessageNotificationsOff && !isMentionNotificationsOn) {
        // Off
        badge = NotificationsConstants.NOTIFICATIONS_BADGE_MUTE;
      }
      break;
    default:
      break;
  }

  return badge;
}

/*
 * Get Global Notification State
 * @param  {Object} features - map of features
 * @returns {string} a value that indicates the global notification type that is based on global notification features
 */
export function getGlobalNotificationState(features) {
  let globalNotificationState = NotificationsConstants.NOTIFICATIONS_OFF;
  const items = features.get('items');

  if (features && items && items.size > 0) {
    const groupMessageNotifications = items.get(NotificationsConstants.FEATURES_GROUP_MESSAGE_NOTIFICATIONS);
    const mentionNotifications = items.get(NotificationsConstants.FEATURES_MENTION_NOTIFICATIONS);

    if (groupMessageNotifications) {
      globalNotificationState = NotificationsConstants.NOTIFICATIONS_ALL;
    }
    else if (mentionNotifications) {
      globalNotificationState = NotificationsConstants.NOTIFICATIONS_MENTIONS;
    }
  }

  return globalNotificationState;
}
